import {
    ActivityParentEntityId,
    formatPhoneNumber,
    PhoneNumberE164,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { PhoneCallButton } from "components/telecom/phone-call-button";
import { PhoneSmsButton } from "components/telecom/phone-sms-button";
import { Spinner } from "components/ui/spinner";
import * as React from "react";
import { useAutoComplete } from "store/selectors/hooks";

interface PhoneInputE164OwnProps {
    label?: string;
    initial?: PhoneNumberE164 | null;
    onCommit?: (newValue: PhoneNumberE164) => void;
    placeholder?: string;
    disabled?: boolean;
    showButtons?: boolean;
    required?: boolean;
    entityId?: ActivityParentEntityId;
    isFaxNumber?: boolean;
}

type Action =
    | { type: "change"; value: string }
    | { type: "blur"; value: string }
    | { type: "updateUpstream"; value: string };

interface ComponentState {
    upstreamValue: string;
    mode: "pristine" | "dirty" | "pending";
    currentValue: string;
}

const invalidAction = (action: Action): never => {
    throw new Error(`unknown action type ${action.type}`);
};
const invalidMode = (mode: ComponentState["mode"]): never => {
    throw new Error(`unknown mode ${mode}`);
};

const reducer: React.Reducer<ComponentState, Action> = (p, a): ComponentState => {
    switch (a.type) {
        case "change":
            return { ...p, mode: "dirty", currentValue: a.value };
        case "blur":
            return {
                ...p,
                mode: p.upstreamValue === a.value ? "pristine" : "pending",
                currentValue: a.value,
            };
        case "updateUpstream":
            switch (p.mode) {
                case "dirty":
                    return { ...p, upstreamValue: a.value };
                case "pending":
                    return {
                        ...p,
                        mode: p.currentValue === a.value ? "pristine" : "pending",
                        upstreamValue: a.value,
                    };
                case "pristine":
                    return { ...p, currentValue: a.value, upstreamValue: a.value };
            }
            return invalidMode(p.mode);
    }
    return invalidAction(a);
};

export const PhoneInputE164: React.FC<PhoneInputE164OwnProps> = props => {
    const {
        label,
        disabled,
        placeholder,
        showButtons = true,
        onCommit,
        required,
        entityId,
        isFaxNumber = false,
    } = props;
    const initial = props.initial || "";
    const [state, dispatch] = React.useReducer(reducer, {
        upstreamValue: initial,
        currentValue: initial,
        mode: initial === toPhoneNumberE164(initial) ? "pristine" : "dirty",
    });
    const autoComplete = useAutoComplete();
    const inputValue = formatPhoneNumber(state.currentValue);

    const handleBlur = React.useCallback(() => {
        if (state.mode === "dirty" && onCommit) {
            const toCommit = toPhoneNumberE164(inputValue);
            dispatch({ type: "blur", value: toCommit });
            onCommit(toCommit);
        }
    }, [dispatch, inputValue, state.mode, onCommit]);

    const handleChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({ type: "change", value: e.target.value });
        },
        [dispatch],
    );

    React.useEffect(() => {
        dispatch({ type: "updateUpstream", value: initial });
    }, [dispatch, initial]);

    return (
        <div className="w-full flex space-x-1 lg:space-x-2 items-end">
            <label className="flex-1">
                {label ? <div className="mb-1">{label}</div> : null}
                <div className="relative">
                    <span className="box flex-1">
                        <input
                            value={inputValue}
                            disabled={!!disabled}
                            placeholder={placeholder}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            type="tel"
                            autoComplete={autoComplete}
                            required={required}
                            className="form-input rounded-sm md:rounded w-full disabled:bg-gray-100 border-gray-300 hover:border-brand-600 active:border-brand-400 focus:ring-2 focus:ring-brand-300"
                        />
                    </span>
                    {state.mode === "pending" ? (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <Spinner />
                        </div>
                    ) : null}
                </div>
            </label>
            {showButtons && !isFaxNumber ? (
                <>
                    <div className="flex-none mb-2">
                        <PhoneCallButton value={inputValue} entityId={entityId} />
                    </div>
                    <div className="flex-none mb-2">
                        <PhoneSmsButton value={inputValue} />
                    </div>
                </>
            ) : null}
        </div>
    );
};
